import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import useAuth from '../../../context/auth.context.jsx';
import { stashPendingReferral } from '../../utils/referral.js';

const PENDING_KEY = 'pendingInviteCode';

/**
 * Landing page for an invite link/QR (`/join/:code` — see the invite card in
 * pages/group.jsx). Just stashes the code and hands off:
 * - signed in → straight to the dashboard, where pendingInviteConsumer.jsx
 *   (mounted inside GroupProvider) picks it up and joins.
 * - signed out → to /register first; the same consumer runs after
 *   register/login succeeds, since both go through the same signed-in shell.
 *
 * The link may also carry `?i=<referralCode>` — the sharer's personal
 * referral code, stamped by pages/group.jsx onto the same invite link. That
 * one is stashed separately (utils/referral.js) and consumed at account
 * creation only, never at group-join time — so it survives whether or not
 * this visitor already has an account.
 */
export default function JoinByCode() {
    const { code } = useParams();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (code) localStorage.setItem(PENDING_KEY, code);
        stashPendingReferral(searchParams.get('i'));
        navigate(user ? '/' : '/register', { replace: true });
    }, [code, searchParams, user, navigate]);

    return (
        <div className="route-loading" role="status" aria-live="polite">
            <span className="spinner-border" aria-hidden="true" />
        </div>
    );
}
